import { Link, useLocation } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import './Footer.css'

function Footer() {
  const currentYear = new Date().getFullYear()
  const { pathname } = useLocation()
  const qrUrl = `https://labspecialists.com${pathname}`

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToSection = (hash: string) => {
    // If we're already on the services page, scroll to the section
    if (window.location.pathname === '/services' && hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''))
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }

  const handleServiceLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    const target = e.currentTarget.getAttribute('href')
    if (target && target.startsWith('/services')) {
      // If we're navigating to services page, wait for navigation then scroll
      if (window.location.pathname !== '/services') {
        // Store the hash to scroll to after navigation
        sessionStorage.setItem('scrollToSection', hash)
      } else {
        // Already on services page, scroll immediately
        scrollToSection(hash)
      }
    }
  }

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-heading">Laboratory Specialists</h3>
            <p className="footer-text">
              Professional laboratory equipment installation services for businesses and universities.
            </p>
          </div>

          <div className="footer-section">
            <h4 className="footer-subheading">Quick Links</h4>
            <ul className="footer-links">
              <li>
                <Link to="/" onClick={scrollToTop}>Home</Link>
              </li>
              <li>
                <Link to="/services" onClick={scrollToTop}>Services</Link>
              </li>
              <li>
                <Link to="/products" onClick={scrollToTop}>Products</Link>
              </li>
              <li>
                <Link to="/about" onClick={scrollToTop}>About</Link>
              </li>
              <li>
                <Link to="/booking" onClick={scrollToTop}>Book Consultation</Link>
              </li>
              <li>
                <a href="/assets/lab-spec-brochure-2026.pdf" target="_blank" rel="noopener noreferrer">Brochure (PDF)</a>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="footer-subheading">Services</h4>
            <ul className="footer-links">
              <li>
                <Link 
                  to="/services#equipment-installation" 
                  onClick={(e) => handleServiceLinkClick(e, '#equipment-installation')}
                >
                  Equipment Installation
                </Link>
              </li>
              <li>
                <Link 
                  to="/services#consultation-planning" 
                  onClick={(e) => handleServiceLinkClick(e, '#consultation-planning')}
                >
                  Consultation & Planning
                </Link>
              </li>
              <li>
                <Link to="/training" onClick={scrollToTop}>Training & Consultation</Link>
              </li>
              <li>
                <Link to="/safety-audit" onClick={scrollToTop}>Safety Audit</Link>
              </li>
              <li>
                <Link to="/casework-blueprints" onClick={scrollToTop}>Casework Blueprints & Specs</Link>
              </li>
              <li>
                <Link to="/general-contractors" onClick={scrollToTop}>For General Contractors</Link>
              </li>
              <li>
                <Link to="/facilities-managers" onClick={scrollToTop}>For Facilities Managers</Link>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="footer-subheading">Contact</h4>
            <ul className="footer-contact">
              <li>
                <a href="tel:+15416589374">(541) 658-9374</a>
              </li>
              <li>
                <a href="mailto:info@labspecialists.com">info@labspecialists.com</a>
              </li>
            </ul>
            <div className="footer-qr">
              <QRCodeSVG
                value={qrUrl}
                size={96}
                bgColor="transparent"
                fgColor="rgba(255,255,255,0.85)"
              />
              <p className="footer-qr-label">{qrUrl.replace('https://', '')}</p>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">
            &copy; {currentYear} Laboratory Specialists. All rights reserved.
          </p>
          <div className="footer-legal">
            <img
              src="/assets/logo_email_signature.png"
              alt="Laboratory Specialists"
              className="footer-legal-logo"
              loading="lazy"
              decoding="async"
            />
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
